export type WidthGroup = { width: number; count?: number };

export type PartingPart = {
  suffix: string;
  width: number;
  weight: number;
  wt_per_m: number;
  thickness: number | null;
  length: number | null;
};

export type PartingResult = {
  parts: PartingPart[];
  totalParts: number;
  usedWidth: number;
  scrapWidth: number;
  widthExceeded: boolean;
  totalWeight: number;
};

/** Part suffix A, B, … Z, AA… */
export function partSuffix(index1Based: number): string {
  let n = index1Based;
  let s = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s || "A";
}

/**
 * v1 HR parting: width budget = sum(group widths) (NOT × globalCount).
 * Total parts = groups.length × globalCount; wt/mm split by globalCount.
 */
export function calculateParting(input: {
  coilWidth: number;
  coilWeight: number;
  coilWtPerM: number | null;
  thickness: number | null;
  length: number | null;
  widthGroups: WidthGroup[];
  globalCount: number;
  maxPartWidth?: number | null;
}): PartingResult {
  const globalCount = Math.max(1, Math.floor(input.globalCount || 1));
  const groups = input.widthGroups.filter((g) => g.width > 0);
  const usedWidth = groups.reduce((s, g) => s + g.width, 0);
  const scrapWidth = Math.max(0, input.coilWidth - usedWidth);
  const widthExceeded = usedWidth > input.coilWidth + 1e-6;
  const coilWtmm =
    input.coilWtPerM && input.coilWtPerM > 0
      ? input.coilWtPerM
      : input.coilWidth > 0
        ? (input.coilWeight * 1000) / input.coilWidth
        : 0;
  const adjustedWtmm = coilWtmm / globalCount;

  const parts: PartingPart[] = [];
  let idx = 0;
  for (const g of groups) {
    for (let i = 0; i < globalCount; i++) {
      idx += 1;
      const weight = (adjustedWtmm * g.width) / 1000;
      parts.push({
        suffix: partSuffix(idx),
        width: g.width,
        weight,
        wt_per_m: adjustedWtmm,
        thickness: input.thickness,
        length: input.length,
      });
    }
  }

  const maxW = input.maxPartWidth;
  const machineExceeded =
    maxW != null && maxW > 0 && parts.some((p) => p.width > maxW + 1e-6);

  return {
    parts,
    totalParts: parts.length,
    usedWidth,
    scrapWidth,
    widthExceeded: widthExceeded || machineExceeded,
    totalWeight: parts.reduce((s, p) => s + p.weight, 0),
  };
}
